<template>
    <div class="inputs-basic-info">
        <CoreTwoColumnsRow v-if="showDnbSearch">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                >
                    {{ $t('§eq_operator.search_dnb') }}
                </CoreBorderWrapper>
            </template>

            <template #right>
                <CoreTwoColumnsRow class="flex-grow-1 flex-shrink-1 flex-basis-45">
                    <template #left>
                        <CoreStringInput
                            class="flex-grow-1"
                            :condensed="true"
                            :disabled="clientStore.searchingDnb || isPlaceholder"
                            v-model="dnbSearch"
                        />
                    </template>

                    <template #right>
                        <CoreButton
                            :label="$t('§eq_operator.search_dnb')"
                            :disabled="dnbSearch.length < 5 || clientStore.searchingDnb || isPlaceholder"
                            :condensed="true"
                            @click="searchDnb"
                        />
                    </template>
                </CoreTwoColumnsRow>
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow>
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                >
                    {{ $t('§eq_operator.name') }}
                    {{ isFieldRequired('name') }}
                </CoreBorderWrapper>
            </template>

            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-45"
                    placeholder="§ui.placeholder_eq_operator_name"
                    ref="nameInput"
                    name="name"
                    :condensed="true"
                    :disabled="
                        clientStore.saving ||
                        isPlaceholder ||
                        (showDnbSearch && clientStore.searchingDnb)
                    "
                    :validator="v$.name"
                    v-model="clientStore.clientData.name"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow>
            <template #left>
                <CoreBorderWrapper
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    :condensed="true"
                >
                    {{ $t('§eq_operator.vat_identification_number') }}
                    <span
                        v-if="!clientStore.clientData.company_registration_number"
                        class="text-danger"
                    >
                        &nbsp;*
                    </span>
                </CoreBorderWrapper>
            </template>

            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-45"
                    :placeholder="placeholderFor('vat_identification_number', '§ui.placeholder_input_vat_id_number')"
                    ref="vat_identification_numberInput"
                    name="vat"
                    :condensed="true"
                    :disabled="
                        clientStore.saving ||
                        isPlaceholder ||
                        (showDnbSearch && clientStore.searchingDnb)
                    "
                    :validator="v$.vat_identification_number"
                    v-model="clientStore.clientData.vat_identification_number"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow>
            <template #left>
                <CoreBorderWrapper
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    :condensed="true"
                >
                    <p class="flex flex-col">
                        <span
                            >{{ $t('§eq_operator.company_registration_number') }}
                            <span
                                v-if="!clientStore.clientData.vat_identification_number"
                                class="text-danger"
                            >
                                &nbsp;*
                            </span>
                        </span>
                        <span class="text-100 text-dark-muted font-italic">
                            {{ $t('§eq_operator.vat_or_company_registration_required') }}
                        </span>
                    </p>
                </CoreBorderWrapper>
            </template>

            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-45"
                    :placeholder="placeholderFor('company_registration_number', '§ui.placeholder_input_company_registration_number')"
                    ref="company_registration_numberInput"
                    name="company_registration_number"
                    :condensed="true"
                    :disabled="
                        clientStore.saving ||
                        isPlaceholder ||
                        (showDnbSearch && clientStore.searchingDnb)
                    "
                    :validator="v$.company_registration_number"
                    v-model="clientStore.clientData.company_registration_number"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow v-if="showDnbSearch">
            <template #left>
                <CoreBorderWrapper
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    :condensed="true"
                >
                    {{ $t('§eq_operator.duns') }}
                </CoreBorderWrapper>
            </template>

            <template #right>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-45"
                >
                    {{ clientStore.clientData.duns }}
                </CoreBorderWrapper>
            </template>
        </CoreTwoColumnsRow>
        <CoreTwoColumnsRow v-if="showDnbSearch">
            <template #left>
                <CoreBorderWrapper
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    :condensed="true"
                >
                    {{ $t('§eq_operator.website_url') }}
                </CoreBorderWrapper>
            </template>

            <template #right>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-45"
                >
                    <span v-if="clientStore.clientData.web_url_missing">
                        {{ $t('§eq_operator.dnb_field_not_reported', { field: $t('§eq_operator.website_url') }) }}
                    </span>
                    <span v-else>
                        {{ clientStore.clientData.web_url }}
                    </span>
                </CoreBorderWrapper>
            </template>
        </CoreTwoColumnsRow>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import rules from '../validationRules.js'
import { useClientStore } from '@/stores/client'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import { useModalStore } from '@/stores/modal'


export default {
    name: 'InputsBasicInfo',

    props: {
        showDnbSearch: {
            type: Boolean,
            default: true,
        },
    },

    components: {
        CoreBorderWrapper,
        CoreTwoColumnsRow,
        CoreStringInput,
        CoreButton,
    },

    data() {
        return {
            v$: {},
            dnbSearch: '',
            clientStore: useClientStore(),
            modalStore: useModalStore(),
            externalErrors: {
                vat_identification_number: [],
                company_registration_number: [],
            },
        }
    },

    computed: {
        isPlaceholder() {
            return !!this.clientStore.clientData.is_dummy
        },

        isValid() {
            const hasVatOrCompanyReg =
                !!this.clientStore.clientData.vat_identification_number ||
                !!this.clientStore.clientData.company_registration_number

            return !this.v$.$invalid && hasVatOrCompanyReg
        },
    },

    watch: {
        isValid: {
            handler(newValue) {
                this.clientStore.isValidBasicInfo = newValue
            },
            immediate: true,
        },
        'clientStore.clientData.vat_identification_number'(newValue) {
            if (this.externalErrors.vat_identification_number.length > 0) {
                this.externalErrors.vat_identification_number = []
            }
            if (
                this.externalErrors.company_registration_number.length > 0 &&
                newValue
            ) {
                this.externalErrors.company_registration_number = []
            }
            if (!newValue) {
                this.v$.vat_identification_number?.$reset()
            }
        },
        'clientStore.clientData.company_registration_number'(newValue) {
            if (this.externalErrors.company_registration_number.length > 0) {
                this.externalErrors.company_registration_number = []
            }
            if (
                this.externalErrors.vat_identification_number.length > 0 &&
                newValue
            ) {
                this.externalErrors.vat_identification_number = []
            }
            if (!newValue) {
                this.v$.company_registration_number?.$reset()
            }
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.v$ = useVuelidate(rules(), this.clientStore.clientData, {
                $externalResults: this.externalErrors,
            })

            if (this.$refs.nameInput && typeof this.$refs.nameInput.focus === 'function') {
                this.$refs.nameInput.focus()
            }
        })
    },

    methods: {
        placeholderFor(fieldName, defaultKey) {
            if (!this.showDnbSearch) return defaultKey
            return this.clientStore.dnbMissingFields.includes(fieldName)
                ? '§ui.placeholder_dnb_not_obtained'
                : defaultKey
        },

        setExternalValidationErrors(errors) {
            if (!Array.isArray(errors) || errors.length === 0) {
                return
            }

            errors.forEach((error) => {
                const { field, message } = error

                if (this.externalErrors.hasOwnProperty(field)) {
                    this.externalErrors[field] = [message]

                    if (this.v$[field]) {
                        this.v$[field].$touch()
                    }
                }
            })

            if (errors[0] && errors[0].field) {
                this.focusField(errors[0].field)
            }
        },

        focusField(fieldName) {
            this.$nextTick(() => {
                const refName = `${fieldName}Input`
                if (this.$refs[refName] && typeof this.$refs[refName].focus === 'function') {
                    this.$refs[refName].focus()
                }
            })
        },

        markVatAndCompanyRegAsInvalid() {
            this.externalErrors.vat_identification_number = [
                this.$t('validation.vat_or_company_reg_required'),
            ]
            this.externalErrors.company_registration_number = [
                this.$t('validation.vat_or_company_reg_required'),
            ]

            if (this.v$.vat_identification_number) {
                this.v$.vat_identification_number.$touch()
            }
            if (this.v$.company_registration_number) {
                this.v$.company_registration_number.$touch()
            }
        },

        clearExternalErrors() {
            this.externalErrors.vat_identification_number = []
            this.externalErrors.company_registration_number = []
        },

        async searchDnb() {
            const results = await this.clientStore.dnbSearch(this.dnbSearch)
            if (results.length > 0)
            {
                this.modalStore.setOpen({
                    name: 'DnbSearchResultsModal',
                    title: '§eq_operator.search_dnb',
                    size: 'large',
                    data: { results },
                })
            }
        },

        focusVatInput() {
            this.$nextTick(() => {
                const vatInput = this.$refs.vat_identification_numberInput
                if (vatInput && typeof vatInput.focus === 'function') {
                    vatInput.focus()
                }
            })
        },
    },
}
</script>

<style scoped>
.inputs-basic-info {
    display: relative;
    z-index: 101;
}
</style>
